import { Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { Vehicle, VehiclesResolve } from '@shared/models';
import { ArivoDialogComponent } from '@shared/components/ui-elements';
import { ActivatedRoute } from '@angular/router';
import { formatLicensePlate, showSpinner, TypedRoute } from '@shared/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VehiclesApiService } from './vehicles-api.service';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { AmplitudeEventService, FeatureFlagService } from '@core/services';
import { ContractEditOpened } from '@integrations/amplitude';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrl: './vehicles.component.scss',
})
export class VehiclesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  showShortTermParking: boolean = false;
  vehicles: Vehicle[] = [];

  @ViewChild('deleteVehicleDialog') deleteVehicleDialog!: ArivoDialogComponent;
  deleteVehicleItem?: Vehicle;

  constructor(
    private toastService: ToastService,
    private translateService: TranslateService,
    private vehiclesApiService: VehiclesApiService,
    private amplitudeEventService: AmplitudeEventService,
    private featureFlagService: FeatureFlagService,
    @Inject(ActivatedRoute) private route: TypedRoute<VehiclesResolve>,
  ) {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.vehicles = data.vehiclesApiResponse;
    });
  }

  ngOnInit() {
    this.showShortTermParking = this.featureFlagService.getFeatureFlag('enable_ppu') === true;
  }

  openDeleteVehicleDialog(event: MouseEvent, vehicle: Vehicle): void {
    event.stopPropagation();
    if (vehicle.garage_contracts.length === 0) {
      this.deleteVehicleItem = vehicle;
      this.deleteVehicleDialog.open = true;
    }
  }

  closeDeleteVehicleDialog(): void {
    this.deleteVehicleItem = undefined;
    this.deleteVehicleDialog.open = false;
  }

  deleteVehicle(): void {
    const vehicle = this.deleteVehicleItem;
    if (vehicle) {
      this.deleteVehicleItem = undefined;
      this.deleteVehicleDialog.open = false;

      this.vehiclesApiService
        .deleteVehicle(vehicle.id!)
        .pipe(showSpinner())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.vehicles = this.vehicles.filter((item) => item.id !== vehicle.id);

            this.toastService.add({
              message: this.translateService.instant('Vehicles.DeleteVehicleSuccess', {
                licensePlate: formatLicensePlate(vehicle.license_plate, vehicle.license_plate_country),
              }),
              type: 'success',
            });
          },
        });
    }
  }

  sendContractEditOpenedEventToAmplitude(): void {
    this.amplitudeEventService.trackEvent(
      new ContractEditOpened({
        referredByPage: 'vehicles',
      }),
    );
  }
}
